import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FaCheck,
  FaCalendarDay,
  FaClock,
  FaCreditCard,
  FaStore,
  FaPiggyBank,
  FaUserHair,
  FaHouse,
  FaChevronLeft,
} from "react-icons/fa6";
import { FaExclamationCircle, FaMapMarkerAlt, FaUser } from "react-icons/fa";
import { FaInfoCircle } from "react-icons/fa";

const SucessfulPaymentSalon = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { orderData, orderId } = location.state || {};
  
  const { currentHomeSalonOrder, homeSalonOrders } = useSelector(
    (state) => state.OrderSalon
  );
  
  const salonOrder = orderData || currentHomeSalonOrder || homeSalonOrders[0];

  useEffect(() => {
    const handleBackButton = (e) => {
      e.preventDefault();
      navigate("/");
    };
    
    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener("popstate", handleBackButton);
    
    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [navigate]);

  const formatServiceDate = (dateString) => {
    if (!dateString) return "Not scheduled";
    try {
      const [day, month, year] = dateString.split("-");
      const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];
      return `${parseInt(day)} ${months[parseInt(month) - 1]} ${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const formatServiceTime = (timeString) => {
    if (!timeString) return "Not scheduled";
    return timeString;
  };

  const calculateSavings = () => {
    if (!salonOrder?.OrderProducts) return 0;
    
    return salonOrder.OrderProducts.reduce((total, product) => {
      const mrp = product.Mrp || product.mrp || 0;
      const price = product.Price || product.price || product.TotalAmount || 0;
      return total + (mrp - price);
    }, 0);
  };

  const PriceBreakdownItem = ({ label, value, type = "normal" }) => (
    <div className="d-flex justify-content-between mb-2">
      <span className={`${type === "total" ? "fw-bold text-dark fs-6" : "text-secondary"}`}>
        {label}
      </span>
      <span className={`${
        type === "discount" ? "text-success fw-semibold" : 
        type === "total" ? "fw-bold text-dark fs-5" : "text-dark"
      }`}>
        {value}
      </span>
    </div>
  );

  if (!salonOrder) {
    return (
      <div className="min-vh-100 bg-white">
        <div className="py-4 position-sticky top-0 z-3 bg-primary border-bottom border-primary">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-auto">
                <button
                  onClick={() => navigate("/")}
                  className="btn btn-link text-white p-0 border-0"
                >
                  <FaChevronLeft size={24} />
                </button>
              </div>
              <div className="col">
                <h5 className="mb-0 fw-bold text-white">Booking Confirmed</h5>
              </div>
              <div className="col-auto" style={{ width: "40px" }}></div>
            </div>
          </div>
        </div>

        <div className="container py-5">
          <div className="py-5">
            <h5 className="text-secondary mb-3">Loading order details...</h5>
            <button 
              className="btn btn-primary border-0 px-4 py-2"
              onClick={() => navigate("/")}
              style={{ backgroundColor: "#1469A7" }}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const PartnerDetails = () => {
    const partnerName = salonOrder?.partnerName || "To be assigned";
    const partnerNumber = salonOrder?.partnerNumber || "To be assigned";

    return (
      <div className="bg-white border rounded-3 p-4 mb-4">
        <h5 className="fw-bold mb-3 text-dark">Service Partner</h5>
        <div className="d-flex align-items-center">
          <div 
            className="rounded-circle d-flex align-items-center justify-content-center me-3" 
            style={{ 
              width: "50px", 
              height: "50px",
              backgroundColor: "#F5F7FA",
              color: "#1469A7"
            }}
          >
            <FaUser size={24} />
          </div>
          <div className="flex-grow-1">
            <h6 className="fw-bold text-dark mb-1">{partnerName}</h6>
            <p className="text-secondary small mb-1">{partnerNumber}</p>
            <p className="small fst-italic text-primary">
              {partnerName === "To be assigned"
                ? "Partner will be assigned soon"
                : "Your service professional"}
            </p>
          </div>
        </div>
        {partnerName === "To be assigned" && (
          <div className="mt-3 p-3 rounded-2 border border-warning">
            <div className="d-flex align-items-start">
              <FaInfoCircle size={14} className="text-warning mt-1 me-2" />
              <p className="small mb-0 text-warning-dark">
                A qualified professional will be assigned to you before your service time
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const totalMRP = salonOrder?.OrderProducts?.reduce(
    (sum, item) => sum + (item.Mrp || item.mrp || 0),
    0
  ) || 0;

  return (
    <div className="min-vh-100 bg-white">
      {/* Header */}
      <div className="py-4 position-sticky top-0 z-3 bg-primary border-bottom border-primary">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-auto">
              <button
                onClick={() => navigate("/")}
                className="btn btn-link text-white p-0 border-0"
              >
                <FaChevronLeft size={24} />
              </button>
            </div>
            <div className="col">
              <h5 className="mb-0 fw-bold text-white">Booking Confirmed</h5>
            </div>
            <div className="col-auto" style={{ width: "40px" }}></div>
          </div>
        </div>
      </div>

      <div className="container py-4">
        {/* Main Content Grid */}
        <div className="row">
          {/* Left Column: Success, Product Details, Order Summary */}
          <div className="col-lg-8">
            {/* Success Section */}
            <div className="bg-white border rounded-3 p-4 mb-4">
              <div className="d-flex align-items-center">
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center me-4" 
                  style={{ 
                    width: "70px", 
                    height: "70px",
                    backgroundColor: "#E8F5E9"
                  }}
                >
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center" 
                    style={{ 
                      width: "50px", 
                      height: "50px",
                      backgroundColor: "#22bd7b"
                    }}
                  >
                    <FaCheck size={24} className="text-white" />
                  </div>
                </div>
                <div>
                  <h1 className="fw-bold mb-1 text-dark">Booking Successful!</h1>
                  <p className="text-secondary mb-1">
                    Order ID: {orderId?.slice(-8)?.toUpperCase() || salonOrder?._id?.slice(-8)?.toUpperCase() || "N/A"}
                  </p>
                  <p className="text-secondary mb-0">
                    You'll receive a confirmation message shortly
                  </p>
                </div>
              </div>
            </div>

            {/* Service Details */}
            {salonOrder?.OrderProducts?.length > 0 && (
              <div className="bg-white border rounded-3 p-4 mb-4">
                <h5 className="fw-bold mb-3 text-dark">Service Details</h5>
                
                {salonOrder.OrderProducts.map((product, index) => {
                  const price = product.Price || product.price || product.TotalAmount || 0;
                  const mrp = product.Mrp || product.mrp || 0;
                  const discountPercentage = mrp > 0 ? Math.round(((mrp - price) / mrp) * 100) : 0;
                  
                  return (
                    <div key={index} className="border-bottom py-3">
                      <div className="row align-items-start">
                        {/* Left: Product Image and Details */}
                        <div className="col-md-8">
                          <div className="d-flex">
                            <div className="me-3">
                              <img
                                src={product.ImgUrl || product.imageUrl || ""}
                                alt={product.ProductName || product.productName}
                                className="rounded-2"
                                style={{ 
                                  width: "80px", 
                                  height: "80px", 
                                  objectFit: "cover",
                                  border: "1px solid #F0F2F5"
                                }}
                              />
                            </div>
                            <div>
                              <h6 className="fw-bold text-dark mb-1">
                                {product.ProductName || product.productName}
                              </h6>
                              <p className="text-secondary small mb-2">
                                {product.Brand || product.brand}
                              </p>
                              <p className="small text-secondary mb-2">
                                <FaClock size={12} className="me-1" />
                                {product.Duration || product.duration || ""}
                              </p>
                              
                              {product.Services && product.Services.length > 0 && (
                                <div className="mt-2">
                                  {product.Services.slice(0, 3).map((service, serviceIndex) => (
                                    <p key={serviceIndex} className="small text-secondary mb-1">
                                      • {service.service || service}
                                    </p>
                                  ))}
                                  {product.Services.length > 3 && (
                                    <p className="small text-primary fst-italic">
                                      +{product.Services.length - 3} more services
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Right: Price Details */}
                        <div className="col-md-4">
                          <div className="d-flex flex-column">
                            <div className="mb-2">
                              <h6 className="fw-bold text-dark mb-0">₹{price}</h6>
                              {mrp > price && (
                                <p className="text-secondary small text-decoration-line-through mb-1">
                                  ₹{mrp}
                                </p>
                              )}
                            </div>
                            {mrp > price && (
                              <div className="px-3 py-2 rounded" style={{ backgroundColor: "#F0F7FF" }}>
                                <span className="small fw-bold text-primary">
                                  {discountPercentage}% OFF
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Order Summary */}
            <div className="bg-white border rounded-3 p-4 mb-4">
              <h5 className="fw-bold mb-3 text-dark">Booking Summary</h5>
              
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center py-3 border-bottom">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center me-3" 
                      style={{ 
                        width: "40px", 
                        height: "40px",
                        backgroundColor: "#F5F7FA",
                        color: "#1469A7"
                      }}
                    >
                      <FaCalendarDay size={20} />
                    </div>
                    <div>
                      <p className="small text-secondary mb-1 fw-medium">Service Date</p>
                      <p className="fw-bold text-dark mb-0">{formatServiceDate(salonOrder?.ServicePreferedDate)}</p>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="d-flex align-items-center py-3 border-bottom">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center me-3" 
                      style={{ 
                        width: "40px", 
                        height: "40px",
                        backgroundColor: "#F5F7FA",
                        color: "#1469A7"
                      }}
                    >
                      <FaClock size={20} />
                    </div>
                    <div>
                      <p className="small text-secondary mb-1 fw-medium">Service Time</p>
                      <p className="fw-bold text-dark mb-0">{formatServiceTime(salonOrder?.ServicePreferedTime)}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center py-3">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center me-3" 
                      style={{ 
                        width: "40px", 
                        height: "40px",
                        backgroundColor: "#F5F7FA",
                        color: "#1469A7"
                      }}
                    >
                      <FaMapMarkerAlt size={20} />
                    </div>
                    <div>
                      <p className="small text-secondary mb-1 fw-medium">Service Address</p>
                      <p className="fw-bold text-dark mb-0">
                        {salonOrder?.orderDisplayName || salonOrder?.Address || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="d-flex align-items-center py-3">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center me-3" 
                      style={{ 
                        width: "40px", 
                        height: "40px",
                        backgroundColor: "#F5F7FA",
                        color: "#1469A7"
                      }}
                    >
                      <FaCreditCard size={20} />
                    </div>
                    <div>
                      <p className="small text-secondary mb-1 fw-medium">Payment Status</p>
                      <p className="fw-bold text-dark mb-0">{salonOrder?.PaymentStatus || "COD"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Partner Details */}
            <PartnerDetails />
          </div>

          {/* Right Column: Price Breakdown and Additional Info */}
          <div className="col-lg-4">
            {/* Sticky Price Summary */}
            <div className="sticky-top" style={{ top: "100px" }}>
              {/* Price Breakdown Card */}
              <div className="bg-white border rounded-3 p-4 mb-4">
                <h5 className="fw-bold mb-4 text-dark">Price Breakdown</h5>
                
                <div className="mb-3">
                  <PriceBreakdownItem
                    label="Total MRP"
                    value={`₹${totalMRP}`}
                  />
                  
                  {calculateSavings() > 0 && (
                    <PriceBreakdownItem
                      label="Total Discount"
                      value={`-₹${calculateSavings()}`}
                      type="discount"
                    />
                  )}
                  
                  {salonOrder?.StCoinsUsed > 0 && (
                    <PriceBreakdownItem
                      label="ST Coins Used"
                      value={`-₹${salonOrder?.StCoinsUsed || 0}`}
                      type="discount"
                    />
                  )}
                  
                  <PriceBreakdownItem
                    label="Service Charge"
                    value={`₹${salonOrder?.DeliveryCharge || 0}`}
                  />
                </div>
                
                <div className="border-top pt-3">
                  <PriceBreakdownItem
                    label="Total Amount Paid"
                    value={`₹${salonOrder?.GrandTotal || 0}`}
                    type="total"
                  />
                </div>
                
                {calculateSavings() > 0 && (
                  <div className="mt-4 p-3 rounded-2 border border-success">
                    <div className="d-flex align-items-center">
                      <FaPiggyBank size={16} className="text-success me-2" />
                      <span className="small fw-semibold text-success">
                        You saved ₹{calculateSavings()} on this booking
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Info Card */}
              <div className="bg-white border rounded-3 p-4 mb-4">
                <h5 className="fw-bold mb-3 text-dark">Need Help?</h5>
                <div className="mb-3">
                  <p className="small text-secondary mb-2">
                    <strong>Customer Support:</strong> 1800-XXX-XXXX
                  </p>
                  <p className="small text-secondary mb-2">
                    <strong>Email:</strong> support@salon.com
                  </p>
                  <p className="small text-secondary mb-0">
                    <strong>Service Hours:</strong> 8:00 AM - 10:00 PM
                  </p>
                </div>
                
                <div className="p-3 rounded-2 border" style={{ backgroundColor: "#F8F9FA" }}>
                  <div className="d-flex">
                    <FaInfoCircle size={14} className="text-primary me-2 mt-1" />
                    <p className="small mb-0 text-secondary">
                      You can reschedule or cancel your booking up to 2 hours before the service time
                    </p>
                  </div>
                </div>
              </div>

              {/* Back to Home Button */}
              <button
                className="btn w-100 py-3 fw-bold rounded-2 border-0 text-white mb-4"
                style={{ backgroundColor: "#1469A7" }}
                onClick={() => navigate("/")}
              >
                <FaHouse className="me-2" />
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SucessfulPaymentSalon;